/**
 * EntityAutocomplete — wrapper sobre Autocomplete para seleccionar UNA entidad
 * de un dataset grande (cuentas, empleados, centros de costo, etc.) por su id.
 *
 * Estandariza el boilerplate: items {id,label}, value=id, onChange(id),
 * y un botón "limpiar" opcional para los casos con opción vacía
 * (ej. "Sin centro de costo", "Todos").
 */
import { Autocomplete, type AutocompleteItem } from './Autocomplete'

interface Props {
  options: AutocompleteItem[]
  value: string
  onChange: (id: string) => void
  placeholder?: string
  allowClear?: boolean
  clearLabel?: string
  disabled?: boolean
  required?: boolean
  /**
   * Clases del input. Tiene un **default con estilo**, no vacío.
   *
   * 🔴 Sin él, `Autocomplete` renderiza el input con `className=''`: sin ancho y
   * sin borde. Un `<input>` sin ancho CSS mide ~177px (su `size` por defecto),
   * así que **se desborda** de cualquier contenedor más angosto y tapa lo que
   * tenga al lado. Beatriz lo reportó el 2026-09-07 en el modal de comprobante:
   * el campo de centro de costo, metido en un contenedor de 128px dentro de una
   * tabla, se montaba sobre la columna de glosa.
   *
   * Medido: **19 de 22 usos no pasaban `className`**, o sea el campo se veía sin
   * recuadro y desalineado con los inputs vecinos en casi todo el producto. El
   * default vive acá y no en cada llamador para que el próximo uso nazca bien.
   *
   * ⚠️ En tablas densas hay que pasar el estilo compacto (`text-xs`, `py-1`): el
   * default es el de formulario, que es el caso mayoritario.
   */
  className?: string
  id?: string
  'aria-label'?: string
}

/** El mismo estilo que usan los inputs de formulario del resto del producto. */
const INPUT_POR_DEFECTO =
  'w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg ' +
  'text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200'

export function EntityAutocomplete({
  options,
  value,
  onChange,
  placeholder = 'Buscar…',
  allowClear = false,
  clearLabel = 'Limpiar',
  disabled = false,
  required = false,
  className = INPUT_POR_DEFECTO,
  id,
  'aria-label': ariaLabel,
}: Props) {
  const displayValue = options.find(o => o.id === value)?.label ?? ''
  return (
    <div>
      <Autocomplete
        items={options}
        value={value}
        displayValue={displayValue}
        onChange={() => { /* selección vía onSelect */ }}
        onSelect={(item) => onChange(item.id)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={className}
        id={id}
        aria-label={ariaLabel}
      />
      {allowClear && value && !disabled && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="mt-1 text-xs text-content-muted hover:text-content underline"
        >
          {clearLabel}
        </button>
      )}
    </div>
  )
}
